import React, {
  CSSProperties,
  ForwardedRef,
  MouseEventHandler,
  ReactNode,
  forwardRef,
} from 'react';
import styled, { css } from 'styled-components';
import { ColorProps, MarginProps, space } from 'styled-system';

import { useTabContext } from 'containers/TabContext';
import { colors, fontWeights } from 'theme';
import { IDEALIST_UTM_PARAMS } from 'utils/constants/general';
import { hasUTMParam, isExternalURL, pathWithParams } from 'utils/url';

export const linkStyles = css`
  color: ${colors.waterBlue};
  font-weight: ${fontWeights.semiBold};
  word-wrap: break-word;
  &,
  &:active,
  &:visited {
    text-decoration: none;
  }

  &:hover {
    color: ${colors.niceBlue};
    text-decoration: underline;
  }
`;

export type LinkProps = {
  children?: ReactNode;
  className?: string;
  target?: string;
  href: string;
  rel?: string;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  style?: CSSProperties;
  'data-qa-id'?: string;
  tabIndex?: number;
  onClick?: MouseEventHandler;
  disabled?: boolean;
  title?: string;
};

export const Link = styled(
  forwardRef(
    (
      {
        children,
        href,
        eventName,
        eventProperties,
        rel,
        style,
        target,
        ...props
      }: LinkProps,
      ref: ForwardedRef<HTMLAnchorElement>
    ) => {
      const linkRel = isExternalURL(href) ? 'noopener noreferrer' : rel;
      const linkTarget = isExternalURL(href) ? '_blank' : target;
      const isTabbing = useTabContext();

      return (
        <a
          ref={ref}
          href={
            isExternalURL(href) && !hasUTMParam(href)
              ? pathWithParams(href, IDEALIST_UTM_PARAMS)
              : href
          }
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          style={isTabbing ? style : { ...style, outline: 0 }}
          rel={linkRel}
          target={linkTarget}
        >
          {children}
        </a>
      );
    }
  )
)<MarginProps & ColorProps>`
  ${linkStyles}
  ${space}
`;
